import { useState, useContext, useEffect, useCallback } from 'react';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Title from '../../shared/Title';
import FFPaper from '../../shared/FFPaper';
import Button from '@mui/material/Button';
import DropDownButton, { DropDownMenu } from '../../shared/DropDownButton';
import ComparisonBarChart, { ChartProp } from './ComparisonBarChart';
import DownloadButton from '../../shared/DownloadButton';
import ChartContainer from '../../shared/ChartContainer';
import { VisionAwareContext } from '../../../context/visionaware-context';
import BlueSkeleton from '../../shared/BlueSkeleton';
import { useTranslation } from 'react-i18next';

const exportMenus: DropDownMenu[] = [{ label: 'PDF', value: 'pdf' }, { label: 'CSV', value: 'csv' }];

const renderLegend = (legendLabel: any) => {
    return (
        <Stack direction="row">
            <Stack direction="row" sx={{ justifyContent: 'start', alignItems: 'center' }} spacing={1}>
                <Box sx={{
                    borderRadius: '50%',
                    height: 18,
                    width: 22,
                    backgroundColor: 'success.main',
                }} />
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%'
                }}>
                    <Typography sx={{
                        paddingLeft: '5px',
                        color: 'primary.main',
                        fontSize: {
                            xs: '0.7rem',
                            md: '0.8rem',
                            xl: '16px',
                        },
                        
                        fontWeight: '500',
                        textTransform: 'uppercase',
                    }}>
                        {legendLabel.high}
                    </Typography>
                </Box>
            </Stack>
            <Stack direction="row" sx={{ justifyContent: 'start', alignItems: 'center', ml: 3 }} spacing={1}>
                <Box sx={{
                    borderRadius: '50%',
                    height: 18,
                    width: 22,
                    backgroundColor: 'error.main',
                }} />
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%'
                }}>
                    <Typography sx={{
                        paddingLeft: '5px',
                        color: 'primary.main',
                        fontSize: {
                            xs: '0.7rem',
                            md: '0.8rem',
                            xl: '16px',
                        },
                        fontWeight: '500',
                        textTransform: 'uppercase',
                    }}>
                        {legendLabel.low}
                    </Typography>
                </Box>
            </Stack>
        </Stack>
    )
}


const Tab = (activeTab: number, tabIndex: number, label: string, handler: (arg: number) => void) => {
    return (
        <Box
            color="secondary"
            sx={{
                backgroundColor: activeTab === tabIndex ? 'white' : 'transparent',
                borderTopRightRadius: 8,
                borderBottomRightRadius: 8,
                pt: {
                    xs: '10px',
                    sm: '10px',
                    md: '16px',
                    xl: '16px',
                },
                pr: {
                    xs: '2px',
                    sm: '4px',
                    md: '8px',
                    xl: '10px',
                },
                pb: {
                    xs: '10px',
                    sm: '10px',
                    md: '16px',
                    xl: '16px',
                },
                pl: {
                    xs: '2px',
                    sm: '4px',
                    md: '14px',
                    xl: '16px',
                },
                textAlign: 'left',
                fontWeight: '500',
                textTransform: 'uppercase',
                ml: -1,
                boxShadow: (theme) => activeTab === tabIndex ? '1px 1px 3px ' + theme.palette.shadow.main : 'none'
            }}>
            <Button variant="text"
                onClick={() => handler(tabIndex)}
                sx={{
                    fontWeight: '500',
                    textTransform: 'uppercase',
                    color: activeTab === tabIndex ? 'secondary.main' : 'text.primary',
                    fontSize: {
                        xs: 12,
                        md: 16,
                        xl: 16,
                    },
                    textAlign: 'left',
                }}>
                {label}
            </Button>
        </Box>
    )
}

export default function VisionsAwareCard() {
    const { t } = useTranslation();

    const legendLabels = [
        {
            high: t('va_chart_tab_1_legend_high'),
            low: t('va_chart_tab_1_legend_low')
        },
        {
            high: t('va_chart_tab_3_legend_high'),
            low: t('va_chart_tab_3_legend_low')
        },
        {
            high: t('va_chart_tab_2_legend_high'),
            low: t('va_chart_tab_2_legend_low')
        }
    ];


    const { status, data, isFetching } = useContext(VisionAwareContext);
    const [activeTab, setActiveTab] = useState(0);
    const [legendLabel, setLegendLabel] = useState(legendLabels[0]);
    const [unit, setUnit] = useState('USD');
    const [tabData, setTabData] = useState<undefined | ChartProp[]>(undefined);

    const selectTab = useCallback((idx: number) => {
        setActiveTab(idx);
        setLegendLabel(legendLabels[idx]);

        const tabData0 = !data ? undefined : [
            {
                name: data.highestROI.key,
                value: data.highestROI.value
            },
            {
                name: data.lowestROI.key,
                value: data.lowestROI.value
            }
        ];
        const tabData1 = !data ? undefined : [
            {
                name: data.highestRespondedEvents.key,
                value: data.highestRespondedEvents.value
            },
            {
                name: data.lowestRespondedEvents.key,
                value: data.lowestRespondedEvents.value
            }
        ];
        const tabData2 = !data ? undefined : [
            {
                name: data.highestMatchEventNotifications.key,
                value: data.highestMatchEventNotifications.value
            },
            {
                name: data.lowestMatchEventNotifications.key,
                value: data.lowestMatchEventNotifications.value
            }
        ];

        switch (idx) {
            case 0:
                setUnit('USD');
                setTabData(tabData0);
                break;
            case 1:
                setUnit(t('va_chart_tab_3_title'));
                setTabData(tabData1);
                break;
            case 2:
                setUnit(t('va_chart_tab_2_title'));
                setTabData(tabData2);
                break;
        }// eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        selectTab(activeTab);
    }, [activeTab, selectTab]);

    return (

        <Grid item={true} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
            <FFPaper sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                position: 'relative',
                justifyContent: 'stretch',
                border: '0 0 0 0 solid',
                background: 'linear-gradient(to bottom, white, #F4F4F8)',
            }}>
                <Box sx={{
                    pl: 3,
                    pr: 4,
                    display: 'flex',
                    flexWrap: 'wrap',
                    textTransform: 'uppercase'
                }}>

                    <Title info={t('va_chart_tooltip')} >
                        {'Store Performance Metrics'}
                    </Title>

                    <Stack direction="row" sx={{
                        justifyContent: 'space-between',
                        width: '100%',
                        ml: 1,
                        mt: 2,
                        mb: 1,
                        flexWrap: 'wrap',
                    }}>
                        {renderLegend(legendLabel)}

                        <Stack direction="row" spacing={2}

                        >
                        </Stack>
                    </Stack>
                </Box>

                <Box>
                    <div
                        className="visionaware-chart-container"
                        style={{
                            gridTemplateColumns: "75% 1fr",
                        }}
                    >
                        {isFetching && <BlueSkeleton variant="rectangular" width="95%" height="95%" sx={{ ml: 'auto', mr: 'auto' }} />}

                        {status === 'success' && !isFetching &&
                            <Box
                                sx={{
                                    backgroundColor: 'white',
                                    borderRadius: 3,
                                    padding: 3,
                                    borderLeftStyle: 'solid',
                                    borderLeftColor: '#F6F6F6',
                                    borderLeftWidth: 1,
                                    borderTopStyle: 'solid',
                                    borderTopColor: '#F6F6F6',
                                    borderTopWidth: 1,
                                    zIndex: 5

                                }}
                            >
                                <ChartContainer>
                                    <ComparisonBarChart unit={unit} data={tabData} roi={activeTab === 0} />
                                </ChartContainer>
                            </Box>
                        }

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            {Tab(activeTab, 0, t('va_chart_tab_1_title'), selectTab)}
                            {Tab(activeTab, 1, t('va_chart_tab_3_title'), selectTab)}
                            {Tab(activeTab, 2, t('va_chart_tab_2_title'), selectTab)}

                        </Box>
                    </div>
                </Box>

            </FFPaper>
        </Grid>
    )

}
